import React, { useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  ArrowRight,
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  Columns,
  Layers,
  Sparkles,
  Type,
  Layout,
  Maximize2,
  Palette,
  Sliders,
} from 'lucide-react';
import type { BlockNode, ViewportDevice, StyleTargetState } from '../types';
import { UnitNumberField, IconRadioGroup, ColorPickerField } from './StyleControls';
import { BoxModelWidget } from './BoxModelWidget';

export interface StyleManagerPanelProps {
  selectedNode: BlockNode;
  activeBreakpoint?: ViewportDevice;
  activeState?: StyleTargetState;
  onStateChange?: (state: StyleTargetState) => void;
  onUpdateStylesTarget: (
    nodeId: string,
    patch: Record<string, string | undefined>,
    target?: { breakpoint?: ViewportDevice; state?: StyleTargetState }
  ) => void;
  onHoverBoxPart?: (part: 'margin' | 'padding' | null) => void;
}

export function StyleManagerPanel({
  selectedNode,
  activeBreakpoint = 'desktop',
  activeState = 'default',
  onStateChange,
  onUpdateStylesTarget,
  onHoverBoxPart,
}: StyleManagerPanelProps): React.JSX.Element {
  // Collapsible Sectors State
  const [openSectors, setOpenSectors] = useState<Record<string, boolean>>({
    general: true,
    flex: true,
    dimension: true,
    typography: true,
    decorations: true,
    extra: false,
  });

  const toggleSector = (sectorKey: string) => {
    setOpenSectors((prev) => ({ ...prev, [sectorKey]: !prev[sectorKey] }));
  };

  // Determine active styles slice based on target breakpoint and pseudo-state
  const currentSliceKey =
    activeState === 'hover'
      ? 'hover'
      : activeState === 'active'
      ? 'active'
      : activeBreakpoint === 'tablet'
      ? 'tablet'
      : activeBreakpoint === 'mobile'
      ? 'mobile'
      : 'base';

  const activeStyles: Record<string, string> =
    (selectedNode.styles as any)?.[currentSliceKey] ||
    (currentSliceKey === 'tablet' ? (selectedNode.styles as any)?.md || {} : {}) ||
    {};

  const isBase = currentSliceKey === 'base';

  const updateProp = (prop: string, val: string | undefined) => {
    onUpdateStylesTarget(selectedNode.id, { [prop]: val }, { breakpoint: activeBreakpoint, state: activeState });
  };

  const updateBatch = (patch: Record<string, string | undefined>) => {
    onUpdateStylesTarget(selectedNode.id, patch, { breakpoint: activeBreakpoint, state: activeState });
  };

  return (
    <div className="flex flex-col h-full overflow-y-auto no-scrollbar bg-card text-foreground select-none divide-y divide-border">
      {/* Pinned Selector & State Bar */}
      <div className="p-3 bg-muted/30 flex flex-col gap-2 shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] font-mono font-bold text-[#3b97e3] bg-[#3b97e3]/10 px-2 py-0.5 rounded">
              #{selectedNode.htmlId || selectedNode.id.slice(0, 6)}
            </span>
            <span className="text-[10px] text-muted-foreground uppercase font-semibold">
              {activeBreakpoint}
            </span>
          </div>

          {/* State Switcher (- State - | :hover | :active) */}
          <div className="flex items-center gap-1">
            <span className="text-[10px] text-muted-foreground font-semibold">State:</span>
            <select
              value={activeState}
              onChange={(e) => onStateChange && onStateChange(e.target.value as StyleTargetState)}
              className="text-xs px-2 py-0.5 rounded bg-background border border-input text-foreground font-medium cursor-pointer focus:outline-none focus:border-[#3b97e3]"
            >
              <option value="default">- Default -</option>
              <option value="hover">:hover</option>
              <option value="active">:active</option>
            </select>
          </div>
        </div>
      </div>

      {/* SECTOR 1: GENERAL & POSITION */}
      <div className="flex flex-col">
        <button
          type="button"
          onClick={() => toggleSector('general')}
          className="flex items-center justify-between p-3 hover:bg-muted/40 transition-colors text-xs font-bold text-foreground cursor-pointer"
        >
          <div className="flex items-center gap-2">
            <Layout className="h-3.5 w-3.5 text-[#3b97e3]" />
            <span>General & Position</span>
          </div>
          {openSectors.general ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </button>

        {openSectors.general && (
          <div className="px-3 pb-3 flex flex-col gap-3">
            <IconRadioGroup
              label="Display"
              value={activeStyles.display}
              isModified={Boolean(activeStyles.display)}
              onChange={(val) => updateProp('display', val)}
              options={[
                { value: 'block', label: 'Block' },
                { value: 'flex', label: 'Flex' },
                { value: 'grid', label: 'Grid' },
                { value: 'inline-block', label: 'Inline' },
                { value: 'none', label: 'None' },
              ]}
            />

            <div className="grid grid-cols-2 gap-2">
              <div className="flex flex-col gap-1 text-[11px]">
                <label className="text-muted-foreground font-medium">Position</label>
                <select
                  value={activeStyles.position || 'static'}
                  onChange={(e) => updateProp('position', e.target.value === 'static' ? undefined : e.target.value)}
                  className="px-2 py-1 text-xs rounded border border-input bg-background text-foreground cursor-pointer focus:outline-none focus:border-[#3b97e3]"
                >
                  <option value="static">static</option>
                  <option value="relative">relative</option>
                  <option value="absolute">absolute</option>
                  <option value="fixed">fixed</option>
                  <option value="sticky">sticky</option>
                </select>
              </div>

              <UnitNumberField
                label="Z-Index"
                value={activeStyles.zIndex}
                isModified={Boolean(activeStyles.zIndex)}
                onChange={(v) => updateProp('zIndex', v)}
                units={['']}
                defaultUnit=""
              />
            </div>

            {activeStyles.position && activeStyles.position !== 'static' && (
              <div className="grid grid-cols-2 gap-2 p-2 bg-muted/20 rounded border border-border/60">
                <UnitNumberField
                  label="Top"
                  value={activeStyles.top}
                  isModified={Boolean(activeStyles.top)}
                  onChange={(v) => updateProp('top', v)}
                />
                <UnitNumberField
                  label="Right"
                  value={activeStyles.right}
                  isModified={Boolean(activeStyles.right)}
                  onChange={(v) => updateProp('right', v)}
                />
                <UnitNumberField
                  label="Bottom"
                  value={activeStyles.bottom}
                  isModified={Boolean(activeStyles.bottom)}
                  onChange={(v) => updateProp('bottom', v)}
                />
                <UnitNumberField
                  label="Left"
                  value={activeStyles.left}
                  isModified={Boolean(activeStyles.left)}
                  onChange={(v) => updateProp('left', v)}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* SECTOR 2: FLEX & GRID LAYOUT */}
      <div className="flex flex-col">
        <button
          type="button"
          onClick={() => toggleSector('flex')}
          className="flex items-center justify-between p-3 hover:bg-muted/40 transition-colors text-xs font-bold text-foreground cursor-pointer"
        >
          <div className="flex items-center gap-2">
            <Columns className="h-3.5 w-3.5 text-[#3b97e3]" />
            <span>Flex & Grid Layout</span>
          </div>
          {openSectors.flex ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </button>

        {openSectors.flex && (
          <div className="px-3 pb-3 flex flex-col gap-3">
            <IconRadioGroup
              label="Direction"
              value={activeStyles.flexDirection}
              isModified={Boolean(activeStyles.flexDirection)}
              onChange={(val) => updateProp('flexDirection', val)}
              options={[
                { value: 'row', label: 'Row', icon: <ArrowRight className="h-3 w-3" /> },
                { value: 'column', label: 'Column', icon: <ArrowDown className="h-3 w-3" /> },
                { value: 'row-reverse', label: 'Row Rev', icon: <ArrowLeft className="h-3 w-3" /> },
                { value: 'column-reverse', label: 'Col Rev', icon: <ArrowUp className="h-3 w-3" /> },
              ]}
            />

            <div className="grid grid-cols-2 gap-2">
              <UnitNumberField
                label="Gap"
                value={activeStyles.gap}
                isModified={Boolean(activeStyles.gap)}
                onChange={(v) => updateProp('gap', v)}
              />
              <div className="flex flex-col gap-1 text-[11px]">
                <label className="text-muted-foreground font-medium">Flex Wrap</label>
                <select
                  value={activeStyles.flexWrap || 'nowrap'}
                  onChange={(e) => updateProp('flexWrap', e.target.value === 'nowrap' ? undefined : e.target.value)}
                  className="px-2 py-1 text-xs rounded border border-input bg-background text-foreground cursor-pointer focus:outline-none"
                >
                  <option value="nowrap">nowrap</option>
                  <option value="wrap">wrap</option>
                  <option value="wrap-reverse">wrap-reverse</option>
                </select>
              </div>
            </div>

            <div className="flex flex-col gap-1 text-[11px]">
              <label className="text-muted-foreground font-medium">Justify Content</label>
              <select
                value={activeStyles.justifyContent || 'flex-start'}
                onChange={(e) => updateProp('justifyContent', e.target.value)}
                className="px-2 py-1 text-xs rounded border border-input bg-background text-foreground cursor-pointer focus:outline-none"
              >
                <option value="flex-start">start (left)</option>
                <option value="center">center</option>
                <option value="flex-end">end (right)</option>
                <option value="space-between">space-between</option>
                <option value="space-around">space-around</option>
                <option value="space-evenly">space-evenly</option>
              </select>
            </div>

            <div className="flex flex-col gap-1 text-[11px]">
              <label className="text-muted-foreground font-medium">Align Items</label>
              <select
                value={activeStyles.alignItems || 'stretch'}
                onChange={(e) => updateProp('alignItems', e.target.value)}
                className="px-2 py-1 text-xs rounded border border-input bg-background text-foreground cursor-pointer focus:outline-none"
              >
                <option value="stretch">stretch</option>
                <option value="flex-start">start</option>
                <option value="center">center</option>
                <option value="flex-end">end</option>
                <option value="baseline">baseline</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* SECTOR 3: DIMENSION & BOX MODEL */}
      <div className="flex flex-col">
        <button
          type="button"
          onClick={() => toggleSector('dimension')}
          className="flex items-center justify-between p-3 hover:bg-muted/40 transition-colors text-xs font-bold text-foreground cursor-pointer"
        >
          <div className="flex items-center gap-2">
            <Maximize2 className="h-3.5 w-3.5 text-[#3b97e3]" />
            <span>Dimension & Box Model</span>
          </div>
          {openSectors.dimension ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </button>

        {openSectors.dimension && (
          <div className="px-3 pb-3 flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-2">
              <UnitNumberField
                label="Width"
                value={activeStyles.width}
                isModified={Boolean(activeStyles.width)}
                onChange={(v) => updateProp('width', v)}
              />
              <UnitNumberField
                label="Height"
                value={activeStyles.height}
                isModified={Boolean(activeStyles.height)}
                onChange={(v) => updateProp('height', v)}
              />
              <UnitNumberField
                label="Max Width"
                value={activeStyles.maxWidth}
                isModified={Boolean(activeStyles.maxWidth)}
                onChange={(v) => updateProp('maxWidth', v)}
              />
              <UnitNumberField
                label="Min Height"
                value={activeStyles.minHeight}
                isModified={Boolean(activeStyles.minHeight)}
                onChange={(v) => updateProp('minHeight', v)}
              />
            </div>

            {/* Embedded 4-Quadrant Box Model */}
            <div className="pt-2 border-t border-border/60">
              <BoxModelWidget
                values={{
                  marginTop: activeStyles.marginTop,
                  marginRight: activeStyles.marginRight,
                  marginBottom: activeStyles.marginBottom,
                  marginLeft: activeStyles.marginLeft,
                  paddingTop: activeStyles.paddingTop,
                  paddingRight: activeStyles.paddingRight,
                  paddingBottom: activeStyles.paddingBottom,
                  paddingLeft: activeStyles.paddingLeft,
                  width: activeStyles.width,
                  height: activeStyles.height,
                }}
                onChange={updateBatch}
                onHoverBoxPart={onHoverBoxPart}
              />
            </div>
          </div>
        )}
      </div>

      {/* SECTOR 4: TYPOGRAPHY */}
      <div className="flex flex-col">
        <button
          type="button"
          onClick={() => toggleSector('typography')}
          className="flex items-center justify-between p-3 hover:bg-muted/40 transition-colors text-xs font-bold text-foreground cursor-pointer"
        >
          <div className="flex items-center gap-2">
            <Type className="h-3.5 w-3.5 text-[#3b97e3]" />
            <span>Typography</span>
          </div>
          {openSectors.typography ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </button>

        {openSectors.typography && (
          <div className="px-3 pb-3 flex flex-col gap-3">
            <div className="flex flex-col gap-1 text-[11px]">
              <label className="text-muted-foreground font-medium">Font Family</label>
              <select
                value={activeStyles.fontFamily || ''}
                onChange={(e) => updateProp('fontFamily', e.target.value || undefined)}
                className="px-2 py-1 text-xs rounded border border-input bg-background text-foreground cursor-pointer focus:outline-none focus:border-[#3b97e3]"
              >
                <option value="">Inherit / System</option>
                <option value="'Inter', sans-serif">Inter</option>
                <option value="'Plus Jakarta Sans', sans-serif">Plus Jakarta Sans</option>
                <option value="'Poppins', sans-serif">Poppins</option>
                <option value="'Roboto', sans-serif">Roboto</option>
                <option value="'Montserrat', sans-serif">Montserrat</option>
                <option value="'Playfair Display', serif">Playfair Display</option>
                <option value="'Merriweather', serif">Merriweather</option>
                <option value="'Space Grotesk', sans-serif">Space Grotesk</option>
                <option value="'JetBrains Mono', monospace">JetBrains Mono</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <UnitNumberField
                label="Font Size"
                value={activeStyles.fontSize}
                isModified={Boolean(activeStyles.fontSize)}
                onChange={(v) => updateProp('fontSize', v)}
                defaultUnit="px"
              />
              <div className="flex flex-col gap-1 text-[11px]">
                <label className="text-muted-foreground font-medium">Weight</label>
                <select
                  value={activeStyles.fontWeight || ''}
                  onChange={(e) => updateProp('fontWeight', e.target.value || undefined)}
                  className="px-2 py-1 text-xs rounded border border-input bg-background text-foreground cursor-pointer focus:outline-none"
                >
                  <option value="">Default</option>
                  <option value="300">300 Light</option>
                  <option value="400">400 Regular</option>
                  <option value="500">500 Medium</option>
                  <option value="600">600 SemiBold</option>
                  <option value="700">700 Bold</option>
                  <option value="800">800 ExtraBold</option>
                  <option value="900">900 Black</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <UnitNumberField
                label="Line Height"
                value={activeStyles.lineHeight}
                isModified={Boolean(activeStyles.lineHeight)}
                onChange={(v) => updateProp('lineHeight', v)}
                units={['', 'px', 'rem']}
                defaultUnit=""
              />
              <UnitNumberField
                label="Letter Spacing"
                value={activeStyles.letterSpacing}
                isModified={Boolean(activeStyles.letterSpacing)}
                onChange={(v) => updateProp('letterSpacing', v)}
                units={['px', 'em']}
                defaultUnit="px"
              />
            </div>

            <ColorPickerField
              label="Text Color"
              value={activeStyles.color}
              isModified={Boolean(activeStyles.color)}
              onChange={(v) => updateProp('color', v)}
            />

            <IconRadioGroup
              label="Text Align"
              value={activeStyles.textAlign}
              isModified={Boolean(activeStyles.textAlign)}
              onChange={(val) => updateProp('textAlign', val)}
              options={[
                { value: 'left', label: 'Left', icon: <AlignLeft className="h-3 w-3" /> },
                { value: 'center', label: 'Center', icon: <AlignCenter className="h-3 w-3" /> },
                { value: 'right', label: 'Right', icon: <AlignRight className="h-3 w-3" /> },
                { value: 'justify', label: 'Justify', icon: <AlignJustify className="h-3 w-3" /> },
              ]}
            />
          </div>
        )}
      </div>

      {/* SECTOR 5: DECORATIONS */}
      <div className="flex flex-col">
        <button
          type="button"
          onClick={() => toggleSector('decorations')}
          className="flex items-center justify-between p-3 hover:bg-muted/40 transition-colors text-xs font-bold text-foreground cursor-pointer"
        >
          <div className="flex items-center gap-2">
            <Palette className="h-3.5 w-3.5 text-[#3b97e3]" />
            <span>Decorations</span>
          </div>
          {openSectors.decorations ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </button>

        {openSectors.decorations && (
          <div className="px-3 pb-3 flex flex-col gap-3">
            <ColorPickerField
              label="Background Color"
              value={activeStyles.backgroundColor}
              isModified={Boolean(activeStyles.backgroundColor)}
              onChange={(v) => updateProp('backgroundColor', v)}
            />

            <div className="flex flex-col gap-1 text-[11px]">
              <label className="text-muted-foreground font-medium">Gradient Preset</label>
              <select
                value={activeStyles.backgroundImage || ''}
                onChange={(e) => updateProp('backgroundImage', e.target.value || undefined)}
                className="px-2 py-1 text-xs rounded border border-input bg-background text-foreground cursor-pointer focus:outline-none"
              >
                <option value="">None</option>
                <option value="linear-gradient(135deg, #6366f1 0%, #a855f7 100%)">Indigo - Purple</option>
                <option value="linear-gradient(135deg, #0ea5e9 0%, #3b82f6 100%)">Sky - Blue</option>
                <option value="linear-gradient(135deg, #10b981 0%, #059669 100%)">Emerald Glow</option>
                <option value="linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)">Warm Amber</option>
                <option value="linear-gradient(135deg, #0f172a 0%, #1e293b 100%)">Dark Slate</option>
              </select>
            </div>

            <UnitNumberField
              label="Border Radius"
              value={activeStyles.borderRadius}
              isModified={Boolean(activeStyles.borderRadius)}
              onChange={(v) => updateProp('borderRadius', v)}
              units={['px', '%', 'rem']}
              defaultUnit="px"
            />

            <div className="grid grid-cols-2 gap-2">
              <UnitNumberField
                label="Border Width"
                value={activeStyles.borderWidth}
                isModified={Boolean(activeStyles.borderWidth)}
                onChange={(v) => {
                  updateBatch({
                    borderWidth: v,
                    borderStyle: v && !activeStyles.borderStyle ? 'solid' : activeStyles.borderStyle,
                  });
                }}
              />
              <div className="flex flex-col gap-1 text-[11px]">
                <label className="text-muted-foreground font-medium">Border Style</label>
                <select
                  value={activeStyles.borderStyle || 'none'}
                  onChange={(e) => updateProp('borderStyle', e.target.value === 'none' ? undefined : e.target.value)}
                  className="px-2 py-1 text-xs rounded border border-input bg-background text-foreground cursor-pointer focus:outline-none"
                >
                  <option value="none">none</option>
                  <option value="solid">solid</option>
                  <option value="dashed">dashed</option>
                  <option value="dotted">dotted</option>
                  <option value="double">double</option>
                </select>
              </div>
            </div>

            {activeStyles.borderStyle && activeStyles.borderStyle !== 'none' && (
              <ColorPickerField
                label="Border Color"
                value={activeStyles.borderColor}
                isModified={Boolean(activeStyles.borderColor)}
                onChange={(v) => updateProp('borderColor', v)}
              />
            )}

            <div className="flex flex-col gap-1 text-[11px]">
              <label className="text-muted-foreground font-medium">Box Shadow Preset</label>
              <select
                value={activeStyles.boxShadow || ''}
                onChange={(e) => updateProp('boxShadow', e.target.value || undefined)}
                className="px-2 py-1 text-xs rounded border border-input bg-background text-foreground cursor-pointer focus:outline-none"
              >
                <option value="">None</option>
                <option value="0 1px 2px 0 rgba(0, 0, 0, 0.05)">Subtle (SM)</option>
                <option value="0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)">Standard (MD)</option>
                <option value="0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)">Spacious (LG)</option>
                <option value="0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)">Elevated (XL)</option>
                <option value="0 0 25px rgba(59, 151, 227, 0.45)">Helios Blue Glow</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* SECTOR 6: EXTRA & EFFECTS */}
      <div className="flex flex-col">
        <button
          type="button"
          onClick={() => toggleSector('extra')}
          className="flex items-center justify-between p-3 hover:bg-muted/40 transition-colors text-xs font-bold text-foreground cursor-pointer"
        >
          <div className="flex items-center gap-2">
            <Sliders className="h-3.5 w-3.5 text-[#3b97e3]" />
            <span>Extra & Effects</span>
          </div>
          {openSectors.extra ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </button>

        {openSectors.extra && (
          <div className="px-3 pb-3 flex flex-col gap-3">
            <UnitNumberField
              label="Opacity"
              value={activeStyles.opacity}
              isModified={Boolean(activeStyles.opacity)}
              onChange={(v) => updateProp('opacity', v)}
              units={['']}
              defaultUnit=""
              min={0}
              max={1}
              step={0.05}
            />

            <div className="flex flex-col gap-1 text-[11px]">
              <label className="text-muted-foreground font-medium">Cursor</label>
              <select
                value={activeStyles.cursor || 'default'}
                onChange={(e) => updateProp('cursor', e.target.value === 'default' ? undefined : e.target.value)}
                className="px-2 py-1 text-xs rounded border border-input bg-background text-foreground cursor-pointer focus:outline-none"
              >
                <option value="default">default</option>
                <option value="pointer">pointer</option>
                <option value="grab">grab</option>
                <option value="text">text</option>
                <option value="not-allowed">not-allowed</option>
              </select>
            </div>

            <div className="flex flex-col gap-1 text-[11px]">
              <label className="text-muted-foreground font-medium">Transition Preset</label>
              <select
                value={activeStyles.transition || ''}
                onChange={(e) => updateProp('transition', e.target.value || undefined)}
                className="px-2 py-1 text-xs rounded border border-input bg-background text-foreground cursor-pointer focus:outline-none"
              >
                <option value="">None</option>
                <option value="all 0.15s ease">Fast (150ms)</option>
                <option value="all 0.3s ease-in-out">Smooth (300ms)</option>
                <option value="transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)">Spring Transform (200ms)</option>
              </select>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
